/**
 * Client for workers/cron's schedule endpoints.
 *
 * Schedule creation mirrors the buyer's encrypted GA4 connection into the
 * schedule payload so the cron worker can decrypt at fire time. The buyer's
 * SMTP credentials are looked up separately at fire time from the
 * `smtp:<email>` KV record.
 */

import type {
  ScheduleEncryptedConnection,
  ScheduleFrequency,
  ScheduleRecord,
  ScheduleRunRecord,
  ScheduleSource,
  ReportTemplateId,
} from "@agencylens/shared";
import { config } from "./config.js";

/** What the SPA gets back from GET /schedules — sanitised, no
 *  encryptedConnections. */
export type ScheduleListItem = Pick<
  ScheduleRecord,
  | "id"
  | "label"
  | "template"
  | "sources"
  | "recipients"
  | "frequency"
  | "status"
  | "createdAt"
  | "updatedAt"
> & {
  lastRunAt?: string;
  nextRunAt?: string;
};

export interface ScheduleListResponse {
  schedules: ScheduleListItem[];
}

export interface ScheduleCreateInput {
  label: string;
  template: ReportTemplateId;
  sources: ScheduleSource[];
  recipients: string[];
  subjectOverride?: string;
  frequency: ScheduleFrequency;
  encryptedConnections: ScheduleEncryptedConnection[];
}

export interface ScheduleCreateResponse {
  schedule: ScheduleRecord;
}

export interface RunListResponse {
  runs: ScheduleRunRecord[];
}

async function request<T>(
  path: string,
  jwt: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${config.cronWorkerBase}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  const text = await res.text();
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error(`Cron worker non-JSON ${res.status}: ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    const err = data as { error?: string; hint?: string };
    const e = new Error(err.error ?? `HTTP ${res.status}`);
    Object.assign(e, err);
    (e as Error & { status?: number }).status = res.status;
    throw e;
  }
  return data as T;
}

export async function listSchedules(jwt: string): Promise<ScheduleListResponse> {
  return request<ScheduleListResponse>("/schedules", jwt, { method: "GET" });
}

export async function createSchedule(
  jwt: string,
  input: ScheduleCreateInput,
): Promise<ScheduleCreateResponse> {
  return request<ScheduleCreateResponse>("/schedules", jwt, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function deleteSchedule(jwt: string, id: string): Promise<void> {
  await request<{ ok: true }>(`/schedules/${id}`, jwt, { method: "DELETE" });
}

export async function listRuns(jwt: string, id: string): Promise<RunListResponse> {
  return request<RunListResponse>(`/schedules/${id}/runs`, jwt, { method: "GET" });
}
